import { Loader2 } from 'lucide-react'
import { RouterProvider } from '@tanstack/react-router'
import { useAuth } from '@/hooks/use-auth'
import { LoginPage } from '@/routes/login'
import { router } from '@/router'

/**
 * Decides whether to show the console or the sign-in screen.
 *
 * This sits OUTSIDE the router on purpose. Gating inside it — a `beforeLoad`
 * redirect to `/login` — means the router mounts first, its components fire
 * their queries, and every one of them 401s before the redirect lands. That is
 * a burst of failed requests and a visible flash of an empty table on every
 * cold load. Mounting `RouterProvider` only once authenticated means the
 * console's queries cannot run before there is a session for them to use.
 *
 * The consequence is that sign-in has no URL of its own. For a single-screen
 * gate on an internal tool that is the better trade: nothing to deep-link to,
 * and no redirect loop to get wrong.
 */
export function AppGate() {
  const auth = useAuth()

  if (auth.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center bg-s0">
        {/* Deliberately minimal. This is one round trip to /api/auth/me; a
            skeleton of a screen the user may not even be allowed to see would
            be a worse flash than a spinner. */}
        <Loader2
          aria-label="Loading"
          className="size-5 animate-spin text-faint"
        />
      </div>
    )
  }

  // A failed /auth/me (401, or the API being down) is not an error state to
  // render — it just means "not signed in", and the login screen reports any
  // real connectivity problem when the attempt is made.
  if (!auth.data?.authenticated) return <LoginPage />

  return <RouterProvider router={router} />
}
